<template>
  <detail
    :showDesc="false"
    :isEditDesc="false"
    :isEditName="false"
    :data="data"
    :onManager="onManager"
    :extra-info="extraInfo"
    :base-info="baseInfo"
    :hiddenKeys="['project_domain', 'tenant', 'status']" />
</template>

<script>
import jsYaml from 'js-yaml'
import { supportTabs } from '../constants'
export default {
  name: 'ServiceDetail',
  props: {
    onManager: {
      type: Function,
      required: true,
    },
    cloudEnv: {
      type: String,
    },
    data: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      cmOptions: {
        tabSize: 2,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        mode: 'text/x-yaml',
        theme: 'material',
        readOnly: true,
      },
      effectiveConfigRaw: null,
    }
  },
  computed: {
    configEnabled () {
      const serviceName = this.data?.name
      if (!serviceName) return true
      const conf = supportTabs.find(item => item.name === serviceName)
      // 未配置的服务保持兼容：默认展示配置
      if (!conf) return true
      return !!conf.config
    },
    /** 侧滑页中的默认 / 存储配置（config.default） */
    defaultConfig () {
      return this.data?.config?.default || {}
    },
    /** app-options 返回的已生效配置；结构与后端约定可能不一致，仅做浅层兼容 */
    effectiveConfig () {
      const appOptions = this.effectiveConfigRaw
      if (!appOptions || typeof appOptions !== 'object') return {}
      if (appOptions.data != null && typeof appOptions.data === 'object' && !Array.isArray(appOptions.data)) {
        return appOptions.data
      }
      if (Array.isArray(appOptions)) return {}
      return appOptions
    },
    extraInfo () {
      const cfg = this.data?.config
      const hasStoredConfig =
        cfg && typeof cfg === 'object' && !Array.isArray(cfg) && Object.keys(cfg).length > 0
        // 只有运行中的节点才展示配置
      const hasManagerUri = !!this.data?.manager_uri
      const showStored = hasStoredConfig && this.configEnabled
      const showEffective = hasManagerUri || (this.configEnabled && hasStoredConfig)
      if (!showStored && !showEffective) {
        return []
      }
      return [
        {
          field: 'info',
          title: this.$t('common_428'),
          slots: {
            default: ({ row }) => {
              const leftYaml = jsYaml.safeDump(this.defaultConfig || {})
              const rightYaml = jsYaml.safeDump(this.effectiveConfig || {})
              return [
                <div
                  style="display: flex; gap: 12px; align-items: stretch; max-height: 800px;"
                  class="service-detail-config-split">
                  {showStored ? (
                    <div style="flex: 1; min-width: 0; display: flex; flex-direction: column;">
                      <div style="margin-bottom: 8px; font-weight: 500;">{this.$t('system.service_sidepage_config')}</div>
                      <div style="flex: 1; overflow-y: auto;" class="codemirror-h-100">
                        <code-mirror value={leftYaml} options={this.cmOptions} />
                      </div>
                    </div>
                  ) : null}
                  {showEffective ? (
                    <div style="flex: 1; min-width: 0; display: flex; flex-direction: column;">
                      <div style="margin-bottom: 8px; font-weight: 500;">{this.$t('system.service_sidepage_effective_config')}</div>
                      <div style="flex: 1; overflow-y: auto;" class="codemirror-h-100">
                        <code-mirror value={rightYaml} options={this.cmOptions} />
                      </div>
                    </div>
                  ) : null}
                </div>,
              ]
            },
          },
        },
      ]
    },
  },
  watch: {
    'data.type': {
      handler () {
        this.fetchEffectiveConfig()
      },
      immediate: true,
    },
    'data.name': {
      handler () {
        this.fetchEffectiveConfig()
      },
    },
    'data.manager_uri': {
      handler () {
        this.fetchEffectiveConfig()
      },
    },
    cloudEnv: {
      handler () {
        this.fetchEffectiveConfig()
      },
    },
    // 配置更新后 type 可能不变，但需要重新拉取已生效配置（app-options）
    'data.config': {
      handler () {
        this.fetchEffectiveConfig()
      },
      deep: true,
    },
  },
  methods: {
    async fetchEffectiveConfig () {
      const hasManagerUri = !!this.data?.manager_uri
      const cfg = this.data?.config
      const hasStoredConfig =
        cfg && typeof cfg === 'object' && !Array.isArray(cfg) && Object.keys(cfg).length > 0

      if (!hasManagerUri) {
        if (!this.configEnabled) {
          this.effectiveConfigRaw = null
          return
        }
        if (!hasStoredConfig) {
          this.effectiveConfigRaw = null
          return
        }
      }

      const service = this.data?.type || this.data?.name
      if (!service) {
        this.effectiveConfigRaw = null
        return
      }
      try {
        if (this.cloudEnv === 'service') {
          const resp = await new this.$Manager('options', 'v1').list({ params: { service } })
          // 兼容 Manager.list 可能返回 { data: ... } 或直接 data
          this.effectiveConfigRaw = resp?.data || resp
        }
        if (this.cloudEnv === 'baremetalagent') {
          const resp = await new this.$Manager(`baremetalagents/${this.data.id}/app-options`).list()
          // 兼容 Manager.list 可能返回 { data: ... } 或直接 data
          this.effectiveConfigRaw = resp?.data || resp
        }
        if (this.cloudEnv === 'host') {
          const resp = await new this.$Manager(`hosts/${this.data.id}/app-options`).list()
          // 兼容 Manager.list 可能返回 { data: ... } 或直接 data
          this.effectiveConfigRaw = resp?.data || resp
        }
      } catch (e) {
        this.effectiveConfigRaw = null
      }
    },
  },
}
</script>
